"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { RotateCcw } from "lucide-react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { CompareView } from "@/components/compare-view";
import { ComparePicker } from "@/components/compare-picker";
import { RecommendationQuestionnaire } from "@/components/recommendation-questionnaire";
import { useCompareSelection } from "@/hooks/use-compare-selection";
import { API_BASE } from "@/lib/api";
import { buildRecommendationQuery } from "@/lib/algorithm";
import { getMatchScore } from "@/lib/comparison";
import { SCALERS } from "@/lib/scalers";
import { cn, toSlug } from "@/lib/utils";
import type { Answer } from "@/lib/match-explanations";
import type { IdealCpu, IdealGpu, LaptopInformation, LaptopScores, RecommendationsResponse } from "@/lib/types";
type Tab = "choose" | "needs" | "compare";
const QUESTIONNAIRE_PROGRESS_KEY = "compare-questionnaire-progress";

// Shape of sessionStorage "recommendation-request", shared with /recommend and
// the /spec Q&A — whatever the user last answered, wherever they answered it.
type ScoreRequest = {
  country: string;
  max_price?: number;
  allow_refurbished?: boolean;
  reference: Record<string, unknown>;
  scalers: Record<string, number>;
};
type ScoreResponse = {
  rank: number | null;
  match_score: number | null;
  scores: LaptopScores;
};

// Three steps, in order: pick laptops, say what you need them for, compare.
// The middle step is what turns a spec sheet into a recommendation — it runs the
// same questionnaire as /recommend, then scores each picked laptop against the
// answers with the single-laptop endpoint the /spec page uses (hand-picked
// laptops are usually not in the user's top 5, so /rec alone wouldn't score
// them). It can be skipped; the comparison then falls back to specs alone.
//
// Laptops ticked on the results page arrive already scored, so the tray there
// lands straight on the comparison.

function IconArrowLeft() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5 shrink-0"><path fillRule="evenodd" d="M7.78 3.22a.75.75 0 0 1 0 1.06L4.81 7.25H13a.75.75 0 0 1 0 1.5H4.81l2.97 2.97a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Scores each laptop against a request. A laptop that fails to score is kept as-is. */
async function scoreLaptops(items: LaptopInformation[], req: ScoreRequest): Promise<LaptopInformation[]> {
  const query = `?max_price=${encodeURIComponent(String(req.max_price ?? 0))}` + (req.allow_refurbished ? "&allow_refurbished=true" : "");
  return Promise.all(items.map(async it => {
    try {
      const res = await axios.post<ScoreResponse>(`${API_BASE}/recommend/laptop/${encodeURIComponent(req.country)}/${encodeURIComponent(toSlug(it.laptop.series))}/${it.laptop.config_number}/score${query}`, {
        reference: req.reference,
        scalers: req.scalers
      });
      return {
        ...it,
        scores: res.data.scores,
        match_score: res.data.match_score ?? it.match_score,
        rank: res.data.rank ?? it.rank
      };
    } catch {
      return it;
    }
  }));
}
export default function ComparePage() {
  const $ = _c(76);
  const selection = useCompareSelection();
  const [tab, setTab] = useState(null);
  const [hasResults, setHasResults] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [answers, setAnswers] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [brands, setBrands] = useState(t1);
  const [request, setRequest] = useState(null);
  const [idealCpu, setIdealCpu] = useState(null);
  const [idealGpu, setIdealGpu] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [scoreError, setScoreError] = useState(null);
  const [questionnaireRun, setQuestionnaireRun] = useState(0);
  const [freshStart, setFreshStart] = useState(false);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const read = () => {
        const raw = sessionStorage.getItem("compare-selection");
        const rawAnswers = sessionStorage.getItem("recommendation-answers");
        const rawBrands = sessionStorage.getItem("recommendation-brands");
        const rawRequest = sessionStorage.getItem("recommendation-request");
        const rawRecs = sessionStorage.getItem("recommendations");
        const parsed = raw ? JSON.parse(raw) : [];
        setTab(Array.isArray(parsed) && parsed.length >= 2 ? "compare" : "choose");
        if (rawAnswers) {
          setAnswers(JSON.parse(rawAnswers));
        }
        if (rawBrands) {
          setBrands(JSON.parse(rawBrands));
        }
        if (rawRequest) {
          const req = JSON.parse(rawRequest);
          if (req?.reference) {
            setRequest(req);
          }
        }
        if (rawRecs) {
          const recs = JSON.parse(rawRecs);
          setHasResults(true);
          setIdealCpu(recs?.ideal_cpu ?? null);
          setIdealGpu(recs?.ideal_gpu ?? null);
        }
      };
      const load = () => {
        try {
          read();
        } catch {
          setTab("choose");
        }
      };
      load();
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const canCompare = selection.count >= 2;
  let t4;
  if ($[4] !== answers || $[5] !== request || $[6] !== selection.country) {
    t4 = request != null && request.country === selection.country && Object.keys(answers).length > 0;
    $[4] = answers;
    $[5] = request;
    $[6] = selection.country;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const hasContext = t4;
  let t5;
  if ($[8] !== hasContext || $[9] !== selection.items) {
    t5 = hasContext && selection.items.some(_temp);
    $[8] = hasContext;
    $[9] = selection.items;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const personalised = t5;
  const activeTab = tab !== "choose" && tab !== null && !canCompare ? "choose" : tab;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = next => {
      setTab(next);
      setScoreError(null);
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const show = t6;
  let t7;
  if ($[12] !== hasContext || $[13] !== request || $[14] !== selection) {
    t7 = async () => {
      const unscored = selection.items.some(_temp2);
      if (hasContext && request && unscored) {
        setScoring(true);
        selection.replace(await scoreLaptops(selection.items, request));
        setScoring(false);
      }
      show("compare");
    };
    $[12] = hasContext;
    $[13] = request;
    $[14] = selection;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const openComparison = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      try {
        sessionStorage.removeItem(QUESTIONNAIRE_PROGRESS_KEY);
      } catch {}
      setFreshStart(true);
      setScoreError(null);
      setQuestionnaireRun(_temp3);
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const resetQuestionnaire = t8;
  let t9;
  if ($[17] !== selection) {
    t9 = async given => {
      const country = selection.country;
      const merged = {
        ...given,
        country
      };
      const {
        features,
        budget,
        brands: wantedBrands,
        allowRefurbished,
        requireTouchscreen
      } = buildRecommendationQuery(merged);
      const req_0 = {
        country,
        max_price: budget,
        allow_refurbished: allowRefurbished,
        reference: features,
        scalers: SCALERS
      };
      const params = new URLSearchParams();
      params.set("country", country);
      if (budget !== undefined) {
        params.set("max_price", String(budget));
      }
      if (allowRefurbished) {
        params.set("allow_refurbished", "true");
      }
      if (requireTouchscreen) {
        params.set("require_touchscreen", "true");
      }
      wantedBrands.forEach(b => params.append("brands", b));
      setScoring(true);
      setScoreError(null);
      const [scored, recRes] = await Promise.all([scoreLaptops(selection.items, req_0), axios.post(`${API_BASE}/recommend/rec?${params.toString()}`, {
        reference: features,
        scalers: SCALERS
      }).then(_temp4).catch(_temp5)]);
      setScoring(false);
      if (!scored.some(_temp6)) {
        setScoreError("We couldn't score these laptops just now. Please try again.");
        return;
      }
      selection.replace(scored);
      setFreshStart(false);
      setAnswers(merged);
      setBrands(wantedBrands);
      setRequest(req_0);
      setIdealCpu(recRes?.ideal_cpu ?? null);
      setIdealGpu(recRes?.ideal_gpu ?? null);
      try {
        sessionStorage.setItem("recommendation-answers", JSON.stringify(merged));
        sessionStorage.setItem("recommendation-brands", JSON.stringify(wantedBrands));
        sessionStorage.setItem("recommendation-request", JSON.stringify({
          ...req_0,
          brands: wantedBrands,
          require_touchscreen: requireTouchscreen
        }));
        if (recRes) {
          sessionStorage.setItem("recommendations", JSON.stringify(recRes));
          setHasResults(true);
        }
      } catch {}
      show("compare");
    };
    $[17] = selection;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const submitNeeds = t9;
  const t10 = String(selection.count);
  let t11;
  if ($[19] !== t10) {
    t11 = {
      id: "choose",
      label: "Choose laptops",
      enabled: true,
      badge: t10
    };
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = hasContext ? "\u2713" : undefined;
  let t13;
  if ($[21] !== canCompare || $[22] !== t12) {
    t13 = {
      id: "needs",
      label: "Your needs",
      enabled: canCompare,
      badge: t12
    };
    $[21] = canCompare;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== canCompare) {
    t14 = {
      id: "compare",
      label: "Comparison",
      enabled: canCompare
    };
    $[24] = canCompare;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t11 || $[27] !== t13 || $[28] !== t14) {
    t15 = [t11, t13, t14];
    $[26] = t11;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const TABS = t15;
  const heading = activeTab === "compare" ? "How do these stack up?" : activeTab === "needs" ? "What will you use it for?" : "Compare laptops";
  const intro = activeTab === "compare" ? "Here's a side-by-side breakdown to help you decide, without the jargon. Just the bits that matter." : activeTab === "needs" ? "Answer a few quick questions and we'll judge each laptop against how you'll actually use it, not just its spec sheet." : "Pick two to four laptops, tell us what you need, and we'll lay them out side by side.";
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SiteNav />;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== hasResults) {
    t17 = hasResults && <Link href="/recommendations" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"><IconArrowLeft />Back to results</Link>;
    $[31] = hasResults;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== heading) {
    t18 = <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{heading}</h1>;
    $[33] = heading;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== intro) {
    t19 = <p className="mt-2 max-w-2xl text-base leading-relaxed text-muted-foreground">{intro}</p>;
    $[35] = intro;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t17 || $[38] !== t18 || $[39] !== t19) {
    t20 = <header className="plample-rise">{t17}{t18}{t19}</header>;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== TABS || $[42] !== activeTab || $[43] !== openComparison || $[44] !== scoring) {
    t21 = <div role="tablist" aria-label="Compare laptops" className="mt-8 flex max-w-full overflow-x-auto rounded-full bg-card p-1 ring-1 ring-border/70 sm:inline-flex">{TABS.map((t, i) => {
        const active = activeTab === t.id;
        return <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={active} aria-controls="panel-compare" aria-disabled={!t.enabled} title={t.enabled ? undefined : "Add at least 2 laptops first"} onClick={() => {
          if (!t.enabled || scoring) {
            return;
          }
          if (t.id === "compare") {
            openComparison();
          } else {
            show(t.id);
          }
        }} className={cn("inline-flex h-9 shrink-0 items-center gap-2 rounded-full px-3.5 text-sm font-medium whitespace-nowrap transition-colors sm:px-4", active ? "bg-foreground text-background" : t.enabled ? "cursor-pointer text-muted-foreground hover:text-foreground" : "cursor-not-allowed text-muted-foreground/50")}><span className="tabular-nums opacity-60">{i + 1}</span>{t.label}{t.badge && <span className={cn("rounded-full px-1.5 text-[11px] font-semibold tabular-nums", active ? "bg-background/20" : t.badge === "\u2713" ? "bg-brand/15 text-brand" : "bg-surface")}>{t.badge}</span>}</button>;
      })}</div>;
    $[41] = TABS;
    $[42] = activeTab;
    $[43] = openComparison;
    $[44] = scoring;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = `tab-${activeTab ?? "choose"}`;
  let t23;
  if ($[46] !== activeTab || $[47] !== answers || $[48] !== brands || $[49] !== freshStart || $[50] !== hasContext || $[51] !== idealCpu || $[52] !== idealGpu || $[53] !== openComparison || $[54] !== personalised || $[55] !== questionnaireRun || $[56] !== request || $[57] !== scoreError || $[58] !== selection.count || $[59] !== selection.country || $[60] !== selection.items || $[61] !== submitNeeds) {
    t23 = activeTab === null ? <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p> : activeTab === "choose" ? <ComparePicker continueLabel={hasContext ? `Compare ${selection.count} laptops` : "Next: tell us your needs"} onContinue={() => hasContext ? void openComparison() : show("needs")} /> : activeTab === "needs" ? <div className="overflow-hidden rounded-3xl ring-1 ring-border/60"><div className="flex flex-col gap-2 border-b border-border/60 bg-card px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-8"><p className="text-sm text-muted-foreground">{hasContext && !freshStart ? "Your previous answers are filled in. Change anything that's different." : `We'll score your ${selection.count} laptops against your answers.`}</p><div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1"><button type="button" onClick={resetQuestionnaire} className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"><RotateCcw className="size-3.5" />Reset questionnaire</button><button type="button" onClick={() => void openComparison()} className="cursor-pointer whitespace-nowrap text-sm font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline">{hasContext ? "Keep my answers" : "Skip, compare on specs alone"}</button></div></div>{scoreError && <p role="alert" className="bg-destructive/10 px-5 py-3 text-sm font-medium text-destructive sm:px-8">{scoreError}</p>}<RecommendationQuestionnaire key={questionnaireRun} lockedCountry={selection.country} initialAnswers={hasContext && !freshStart ? answers : undefined} persistKey={QUESTIONNAIRE_PROGRESS_KEY} onComplete={submitNeeds} submitLabel="Compare my laptops" finalCtaLabel={"Compare my laptops \u2192"} /></div> : <CompareView items={selection.items} country={selection.country} answers={personalised ? answers : {}} brands={brands} reference={personalised && request ? request.reference as Record<string, number | string | null> : {}} idealCpu={personalised ? idealCpu : null} idealGpu={personalised ? idealGpu : null} personalised={personalised} onStartOver={() => show("needs")} startOverLabel="Change my answers" onPersonalise={() => show("needs")} />;
    $[46] = activeTab;
    $[47] = answers;
    $[48] = brands;
    $[49] = freshStart;
    $[50] = hasContext;
    $[51] = idealCpu;
    $[52] = idealGpu;
    $[53] = openComparison;
    $[54] = personalised;
    $[55] = questionnaireRun;
    $[56] = request;
    $[57] = scoreError;
    $[58] = selection.count;
    $[59] = selection.country;
    $[60] = selection.items;
    $[61] = submitNeeds;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== t22 || $[64] !== t23) {
    t24 = <div id="panel-compare" role="tabpanel" aria-labelledby={t22} className="mt-8">{t23}</div>;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== t20 || $[67] !== t21 || $[68] !== t24) {
    t25 = <main className="px-5 py-12 sm:px-8 sm:py-16"><div className="mx-auto max-w-7xl">{t20}{t21}{t24}</div></main>;
    $[66] = t20;
    $[67] = t21;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SiteFooter />;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== scoring) {
    t27 = scoring && <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95"><p className="text-lg font-semibold tracking-tight text-foreground">Scoring your laptops</p><p className="mt-1 text-sm text-muted-foreground">Checking each one against your answers…</p></div>;
    $[71] = scoring;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t25 || $[74] !== t27) {
    t28 = <div className="min-h-screen bg-background">{t16}{t25}{t26}{t27}</div>;
    $[73] = t25;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  return t28;
}
function _temp6(it_1) {
  return getMatchScore(it_1) != null;
}
function _temp5() {
  return null;
}
function _temp4(r) {
  return r.data;
}
function _temp3(n) {
  return n + 1;
}
function _temp2(it_0) {
  return getMatchScore(it_0) == null;
}
function _temp(it) {
  return getMatchScore(it) != null;
}